import {
  PERSONA_INFLUENCES,
  PERSONA_TEMPERAMENT_AXES,
  renderPersona,
  type ArtistPersona,
  type PersonaInfluence,
  type PersonaTemperamentAxis,
} from "../../domain/index.js";

// pt-BR display labels for the closed persona vocabulary. The vocabulary itself
// is owned by `@studio/domain` — these are display-only and never widen it,
// mirroring `SonicTraitsEditor`'s label maps.
const INFLUENCE_LABELS: Record<PersonaInfluence, string> = {
  the_beatles: "the beatles",
  led_zeppelin: "led zeppelin",
  miles_davis: "miles davis",
  daft_punk: "daft punk",
  nina_simone: "nina simone",
  radiohead: "radiohead",
  james_brown: "james brown",
  johnny_cash: "johnny cash",
  bjork: "björk",
  kraftwerk: "kraftwerk",
  bob_marley: "bob marley",
  aphex_twin: "aphex twin",
};

const AXIS_LABELS: Record<PersonaTemperamentAxis, string> = {
  discipline: "Disciplina",
  boldness: "Ousadia",
  experimental: "Experimentação",
};

// The poles printed under each fader — left = 1, right = 5.
const AXIS_POLES: Record<PersonaTemperamentAxis, { low: string; high: string }> = {
  discipline: { low: "solto", high: "ensaiado" },
  boldness: { low: "contido", high: "ousado" },
  experimental: { low: "convencional", high: "vanguarda" },
};

// Per-step description matching the 1..5 snippet ranges. `3` is the neutral
// centre — declared but injects nothing into the prompt.
const AXIS_STEP_LABELS: Record<PersonaTemperamentAxis, Record<number, string>> = {
  discipline: {
    1: "improvisado",
    2: "pouco ensaiado",
    3: "neutro",
    4: "bem ensaiado",
    5: "meticuloso",
  },
  boldness: {
    1: "muito contido",
    2: "reservado",
    3: "neutro",
    4: "confiante",
    5: "expansivo",
  },
  experimental: {
    1: "convencional",
    2: "quase convencional",
    3: "neutro",
    4: "exploratório",
    5: "vanguardista",
  },
};

const INFLUENCES_MAX = 3;
export const PERSONA_BIO_MAX = 600;

interface Props {
  value: ArtistPersona;
  onChange: (next: ArtistPersona) => void;
}

/**
 * The artist-persona editor — a free-text `bio`, three `temperament` faders and
 * up to three `influences` chips, plus a live preview of `renderPersona`. Placed
 * below the "Identidade sonora" block in `ArtistForm`. Mirrors the look and
 * structure of `SonicTraitsEditor`; the vocabulary is owned by `@studio/domain`.
 *
 * The preview deliberately EXCLUDES `bio` — the bio is editorial and never
 * enters the prompt (spec §12 #1).
 */
export function PersonaEditor({ value, onChange }: Props): JSX.Element {
  const preview = renderPersona(value);
  const bio = value.bio ?? "";
  const influences = value.influences ?? [];
  const atMax = influences.length >= INFLUENCES_MAX;

  function setBio(next: string): void {
    const clipped = next.slice(0, PERSONA_BIO_MAX);
    onChange({ ...value, bio: clipped.length > 0 ? clipped : undefined });
  }

  function toggleInfluence(term: PersonaInfluence): void {
    const has = influences.includes(term);
    if (!has && atMax) return; // max 3 — picking a 4th is a no-op
    const next = has ? influences.filter((t) => t !== term) : [...influences, term];
    onChange({ ...value, influences: next.length > 0 ? next : undefined });
  }

  function setAxis(axis: PersonaTemperamentAxis, next: number | undefined): void {
    const current = value.temperament ?? {};
    const merged = { ...current, [axis]: next };
    if (next === undefined) delete merged[axis];
    const hasAny = Object.values(merged).some((v) => v !== undefined);
    onChange({ ...value, temperament: hasAny ? merged : undefined });
  }

  return (
    <div className="flex flex-col gap-5">
      <BioField value={bio} onChange={setBio} />

      <div className="flex flex-col gap-2 border-l-2 border-border pl-3">
        <AxisHeader num="07" title="Temperamento" hint="como performa · 3 = neutro" />
        <div className="flex flex-col gap-3.5">
          {PERSONA_TEMPERAMENT_AXES.map((axis, i) => (
            <TemperamentAxis
              key={axis}
              num={String(i + 1)}
              axis={axis}
              value={value.temperament?.[axis]}
              onChange={(next) => setAxis(axis, next)}
            />
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2 border-l-2 border-border pl-3">
        <AxisHeader num="08" title="Influências" hint={`até 3 · ${influences.length}/3`} />
        <div className="flex flex-wrap gap-1.5">
          {PERSONA_INFLUENCES.map((term) => {
            const on = influences.includes(term);
            const locked = !on && atMax;
            return (
              <button
                key={term}
                type="button"
                role="checkbox"
                aria-checked={on}
                aria-label={INFLUENCE_LABELS[term]}
                disabled={locked}
                onClick={() => toggleInfluence(term)}
                className={`border px-2.5 py-1 font-mono text-[11px] lowercase tracking-widest2 transition-colors ${
                  on
                    ? "border-butter bg-butter/15 text-butter"
                    : locked
                      ? "cursor-not-allowed border-border/50 text-ink-dim"
                      : "border-border text-ink-muted hover:border-paper hover:text-paper"
                }`}
              >
                {INFLUENCE_LABELS[term]}
              </button>
            );
          })}
        </div>
      </div>

      <PersonaPreview text={preview} />
    </div>
  );
}

function BioField({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}): JSX.Element {
  const len = value.length;
  const near = len >= PERSONA_BIO_MAX - 60;
  return (
    <div className="flex flex-col gap-2 border-l-2 border-border pl-3">
      <div className="flex items-baseline justify-between">
        <AxisHeader num="06" title="Bio" hint="ficha editorial" />
        <span
          className={`font-mono text-[9px] uppercase tracking-widest2 tabular-nums ${
            near ? "text-vinyl" : "text-ink-dim"
          }`}
        >
          {len} / {PERSONA_BIO_MAX}
        </span>
      </div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={PERSONA_BIO_MAX}
        rows={3}
        aria-label="Bio do personagem"
        placeholder="Quem é este personagem? Uma ficha curta — fica só na biblioteca, não vai pro prompt."
        className="w-full resize-y border border-border bg-asphalt/60 px-3 py-2 font-mono text-xs leading-relaxed text-paper outline-none placeholder:text-ink-dim focus:border-paper"
      />
    </div>
  );
}

function TemperamentAxis({
  num,
  axis,
  value,
  onChange,
}: {
  num: string;
  axis: PersonaTemperamentAxis;
  value: number | undefined;
  onChange: (next: number | undefined) => void;
}): JSX.Element {
  // `undefined` reads as the neutral centre on the fader; the "definir" toggle
  // distinguishes "axis not declared" from "axis = 3 (neutral)" — same pattern
  // as the R3.1 energy fader.
  const declared = value !== undefined;
  const shown = value ?? 3;
  const poles = AXIS_POLES[axis];
  const stepLabel = AXIS_STEP_LABELS[axis][shown] ?? "";
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between">
        <span className="flex items-baseline gap-2">
          <span className="font-mono text-[9px] tabular-nums text-vinyl">{num}</span>
          <span className="font-mono text-[10px] uppercase tracking-widest2 text-paper">
            {AXIS_LABELS[axis]}
          </span>
        </span>
        <button
          type="button"
          aria-pressed={declared}
          onClick={() => onChange(declared ? undefined : 3)}
          className={`font-mono text-[9px] uppercase tracking-widest2 transition-colors ${
            declared ? "text-butter hover:text-paper" : "text-ink-dim hover:text-paper"
          }`}
        >
          {declared ? "× limpar" : "+ definir"}
        </button>
      </div>
      <div className="flex items-center gap-3">
        <input
          type="range"
          min={1}
          max={5}
          step={1}
          value={shown}
          aria-label={AXIS_LABELS[axis]}
          aria-valuetext={stepLabel}
          disabled={!declared}
          onChange={(e) => onChange(Number(e.target.value))}
          className="fader flex-1"
        />
        <span
          className={`w-28 text-right font-mono text-[10px] uppercase tracking-widest2 ${
            declared ? "text-butter" : "text-ink-dim"
          }`}
        >
          {declared ? `${String(shown)} · ${stepLabel}` : "não definido"}
        </span>
      </div>
      <div className="flex justify-between px-0.5 font-mono text-[8px] uppercase tracking-widest2 text-ink-dim">
        <span>{poles.low}</span>
        <span>neutro</span>
        <span>{poles.high}</span>
      </div>
    </div>
  );
}

function AxisHeader({
  num,
  title,
  hint,
}: {
  num: string;
  title: string;
  hint?: string;
}): JSX.Element {
  return (
    <div className="flex items-baseline gap-3">
      <span className="font-mono text-[9px] tabular-nums text-vinyl">{num}</span>
      <span className="catalog-num text-paper">{title}</span>
      {hint && (
        <span className="font-mono text-[9px] uppercase tracking-widest2 text-ink-dim">
          · {hint}
        </span>
      )}
    </div>
  );
}

/**
 * A read-only render of `renderPersona`, framed as a tape-label printout — the
 * exact text the persona injects into the prompt. The `bio` is intentionally
 * absent (it is editorial, never rendered — spec §12 #1).
 */
export function PersonaPreview({ text }: { text: string }): JSX.Element {
  return (
    <div
      className="border-l-2 border-oscillo bg-asphalt/60 px-3 py-3"
      aria-label="Prévia da persona"
    >
      <p className="catalog-num mb-1.5 text-oscillo">PRÉVIA · O QUE A PERSONA INJETA</p>
      {text ? (
        <p className="font-mono text-xs leading-relaxed text-paper">{text}</p>
      ) : (
        <p className="font-mono text-xs italic leading-relaxed text-ink-dim">
          sem temperamento nem influências — a persona não muda o prompt
        </p>
      )}
      <p className="mt-2 font-mono text-[8px] uppercase tracking-widest2 text-ink-dim">
        a bio fica de fora — é só editorial
      </p>
    </div>
  );
}
